/* eslint-disable no-undef */
/**
 * Cart persistence utilities for maintaining cart across authentication flows
 * Uses multiple backup mechanisms including URL hash for OAuth redirects
 */

const CART_COOKIE_NAME = 'horwath-cart-backup';
const COOKIE_MAX_AGE = 3600; // 1 hour in seconds
const CART_URL_PARAM = 'cart_data';

/**
 * Save cart data to a cookie for persistence across OAuth redirects
 */
export function saveCartToCookie(cartData: string): void {
  try {
    // Encode the cart data to make it cookie-safe
    const encodedData = encodeURIComponent(cartData);
    
    // Set cookie with SameSite=None and Secure for OAuth redirects
    // Note: In localhost, we can't use Secure, so we use different settings
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const cookieOptions = isLocalhost 
      ? `${CART_COOKIE_NAME}=${encodedData}; max-age=${COOKIE_MAX_AGE}; path=/`
      : `${CART_COOKIE_NAME}=${encodedData}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=None; Secure`;
    
    document.cookie = cookieOptions;
    
  } catch (error) {
    console.error('Error saving cart to cookie:', error);
  }
}

/**
 * Retrieve cart data from cookie
 */
export function getCartFromCookie(): string | null {
  try {
    const cookies = document.cookie.split(';');
    
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === CART_COOKIE_NAME && value) {
        // Decode the cart data
        const decodedData = decodeURIComponent(value);
        return decodedData;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error retrieving cart from cookie:', error);
    return null;
  }
}

/**
 * Clear the cart backup cookie
 */
export function clearCartCookie(): void {
  try {
    // Set cookie with expired date to delete it
    document.cookie = `${CART_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  } catch (error) {
    console.error('Error clearing cart cookie:', error);
  }
}

/**
 * Save cart data to URL hash for OAuth redirects
 */
export function saveCartToUrl(cartData: string): void {
  try {
    // For localhost development, we'll use a different approach
    // Store in sessionStorage as primary backup since URL hash might get lost
    sessionStorage.setItem('horwath-cart-oauth-backup', cartData);
    
    // Also try to save to URL hash as secondary backup
    try {
      const encoded = btoa(cartData);
      const url = new URL(window.location.href);
      url.hash = `${CART_URL_PARAM}=${encoded}`;
      window.history.replaceState(null, '', url.toString());
    } catch (_e) {
      void _e;
      // Could not save to URL hash
    }
  } catch (error) {
    console.error('Error saving cart for OAuth:', error);
  }
}

/**
 * Retrieve cart data from URL hash or OAuth backup
 */
export function getCartFromUrl(): string | null {
  try {
    // First check sessionStorage OAuth backup (most reliable for OAuth redirects)
    const oauthBackup = sessionStorage.getItem('horwath-cart-oauth-backup');
    if (oauthBackup) {
      sessionStorage.removeItem('horwath-cart-oauth-backup');
      return oauthBackup;
    }
    
    // Then check URL hash
    const hash = window.location.hash;
    if (!hash) return null;
    
    // Look for cart data in hash
    const match = hash.match(new RegExp(`${CART_URL_PARAM}=([^&]+)`));
    if (match && match[1]) {
      try {
        // Decode the data
        const decoded = atob(match[1]);
        
        // Clean up URL after retrieving
        const url = new URL(window.location.href);
        url.hash = '';
        window.history.replaceState(null, '', url.toString());
        
        return decoded;
      } catch (e) {
        console.error('Error decoding cart from URL:', e);
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error retrieving cart from URL:', error);
    return null;
  }
}

/**
 * Backup cart from localStorage to all backup locations
 */
export function backupCart(): void {
  try {
    const cartData = localStorage.getItem('horwath-cart');
    
    if (cartData) {
      // Save to sessionStorage as first backup
      sessionStorage.setItem('horwath-cart-backup', cartData);
      
      // Save to cookie as second backup
      saveCartToCookie(cartData);
      
      // Save to URL hash for OAuth redirects
      saveCartToUrl(cartData);
      
      // Also save to window object as last resort
      (window as any).__cartBackup = cartData;
      
    }
  } catch (error) {
    console.error('Error backing up cart:', error);
  }
}

/**
 * Restore cart from backups to localStorage
 */
export function restoreCart(): boolean {
  try {
    // Check if cart already exists in localStorage
    const existingCart = localStorage.getItem('horwath-cart');
    
    // Only skip restoration if cart exists AND is not empty
    if (existingCart && existingCart !== '[]') {
      return false; // Cart already exists, no need to restore
    }
    
    // Try URL hash/OAuth backup first (most reliable for OAuth redirects)
    let cartData = getCartFromUrl();
    
    if (cartData) {
      localStorage.setItem('horwath-cart', cartData);
      // Clear other backups
      sessionStorage.removeItem('horwath-cart-backup');
      sessionStorage.removeItem('horwath-cart-oauth-backup');
      clearCartCookie();
      delete (window as any).__cartBackup;
      return true;
    }
    
    // Try sessionStorage second
    cartData = sessionStorage.getItem('horwath-cart-backup');
    
    if (cartData) {
      localStorage.setItem('horwath-cart', cartData);
      sessionStorage.removeItem('horwath-cart-backup');
      sessionStorage.removeItem('horwath-cart-oauth-backup');
      clearCartCookie(); // Clear cookie backup too
      return true;
    }
    
    // Try cookie as third option
    cartData = getCartFromCookie();
    
    if (cartData) {
      localStorage.setItem('horwath-cart', cartData);
      clearCartCookie();
      sessionStorage.removeItem('horwath-cart-oauth-backup');
      return true;
    }
    
    // Try window object as last resort
    cartData = (window as any).__cartBackup;
    
    if (cartData) {
      localStorage.setItem('horwath-cart', cartData);
      delete (window as any).__cartBackup;
      sessionStorage.removeItem('horwath-cart-oauth-backup');
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error restoring cart:', error);
    return false;
  }
}